/**
 * The chair's yearly packet on the phone (People Part 7): one list, the year’s items, each
 * prefilled from what CIDER already knows and asking only what changed. One item is open at a
 * time; a tap saves it and opens the next. Every answer lands on the record that owns it; the
 * screen only shows the state. Done prints a summary and the first-sign-in card never returns.
 */
import { useEffect, useState, type ReactNode } from 'react';
import {
  Button,
  Chip,
  Field,
  Input,
  formatCalendarDate,
  formatDate,
  formatDateTime,
} from '@cider/ui';
import { api, sentence } from '../../shell/api.js';
import { useLoad } from '../money/shared.js';
import { TabGlyph } from './icons.js';

export type ItemState = 'not started' | 'done' | 'waiting' | 'expired' | 'not applicable';

export interface PacketItem {
  key: string;
  position: number;
  name: string;
  asks: string;
  completedBy: string;
  satisfiedBy: string;
  renews: string;
  renewsYears: number | null;
  required: boolean;
  because: string | null;
  state: ItemState;
  doneAt: string | null;
  recordedByName: string | null;
  naReason: string | null;
  carriedFrom: number | null;
  signature: { typedName: string; at: string; version: string } | null;
  facts: Record<string, unknown>;
}
export interface Packet {
  personId: string;
  name: string;
  festivalYear: number;
  items: PacketItem[];
  required: number;
  done: number;
  left: number;
  percent: number;
  expired: boolean;
  says: string;
  strikeDate: string | null;
  flags: string[];
  /** Item 1, "Your account" — only for somebody who set one up from the packet mail (Oct 2). */
  account?: { setUpAt: string } | null;
}

export const STATE_CHIP: Record<ItemState, 'ok' | 'warn' | 'bad' | 'mute'> = {
  done: 'ok',
  waiting: 'warn',
  expired: 'bad',
  'not applicable': 'mute',
  'not started': 'mute',
};
export const STATE_WORDS: Record<ItemState, string> = {
  done: 'done',
  waiting: 'waiting on the volunteer chairs',
  expired: 'asks again',
  'not applicable': 'n/a',
  'not started': 'open',
};

interface Key {
  id: string;
  kind: 'email' | 'mobile';
  raw: string;
  state: string;
  primary: boolean;
}
interface Completion {
  item: PacketItem;
  packet: Packet;
  needsCode: { keyId: string; kind: string }[];
}

const text = (v: unknown): string => (typeof v === 'string' ? v : '');

/** One item's form, prefilled from its facts; `submit` posts and the parent redraws. */
function ItemForm({
  item,
  packet,
  submit,
  busy,
}: {
  item: PacketItem;
  packet: Packet;
  submit: (payload: Record<string, unknown>) => Promise<Completion | null>;
  busy: boolean;
}): ReactNode {
  const f = item.facts;
  const [v, setV] = useState<Record<string, string>>({});
  const [needsCode, setNeedsCode] = useState<{ keyId: string; kind: string }[]>([]);
  const [code, setCode] = useState('');
  const [codeRefusal, setCodeRefusal] = useState<string | null>(null);
  const [families, setFamilies] = useState<{ householdId: string; names: string[] }[]>([]);
  const get = (k: string, fallback: unknown = ''): string =>
    v[k] ??
    (typeof fallback === 'string'
      ? fallback
      : typeof fallback === 'number'
        ? String(fallback)
        : '');
  const set = (k: string) => (e: { target: { value: string } }) => {
    setV({ ...v, [k]: e.target.value });
  };
  const id = (k: string): string => `pk-${item.key}-${k}`;

  switch (item.key) {
    case 'who_you_are': {
      const keys = (f['keys'] as Key[] | undefined) ?? [];
      const email =
        keys.find((k) => k.kind === 'email' && k.primary) ?? keys.find((k) => k.kind === 'email');
      const mobile =
        keys.find((k) => k.kind === 'mobile' && k.primary) ?? keys.find((k) => k.kind === 'mobile');
      if (needsCode.length > 0)
        return (
          <div className="packet__form">
            <p>
              A six-digit code went to your {needsCode.map((n) => n.kind).join(' and ')}. Type it
              here.
            </p>
            <Field id={id('code')} label="The code">
              <Input
                id={id('code')}
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                }}
              />
            </Field>
            {codeRefusal ? (
              <p className="ui-form__refusal" role="alert">
                {codeRefusal}
              </p>
            ) : null}
            <Button
              kind="primary"
              busy={busy}
              onClick={() => {
                setCodeRefusal(null);
                const first = needsCode[0];
                if (!first) return;
                api<Packet>('POST', `/onboarding/packet/keys/${first.keyId}/confirm`, {
                  code: code.trim(),
                  year: packet.festivalYear,
                })
                  .then(() => {
                    setNeedsCode(needsCode.slice(1));
                    setCode('');
                    return submit({ __reload: true });
                  })
                  .catch((e: unknown) => {
                    setCodeRefusal(sentence(e));
                  });
              }}
            >
              Confirm the code
            </Button>
          </div>
        );
      return (
        <div className="packet__form">
          <Field id={id('given')} label="Given name">
            <Input
              id={id('given')}
              value={get('givenName', f['givenName'])}
              onChange={set('givenName')}
            />
          </Field>
          <Field id={id('family')} label="Family name">
            <Input
              id={id('family')}
              value={get('familyName', f['familyName'])}
              onChange={set('familyName')}
            />
          </Field>
          <Field id={id('preferred')} label="What you go by (if not your given name)">
            <Input
              id={id('preferred')}
              value={get('preferredName', f['preferredName'])}
              onChange={set('preferredName')}
            />
          </Field>
          <Field
            id={id('email')}
            label="Email"
            hint={email?.state === 'verified' ? 'verified' : 'a code will verify it'}
          >
            <Input
              id={id('email')}
              type="email"
              value={get('email', email?.raw)}
              onChange={set('email')}
            />
          </Field>
          <Field
            id={id('mobile')}
            label="Mobile"
            hint={mobile?.state === 'verified' ? 'verified' : 'confirmed at the volunteer desk'}
          >
            <Input
              id={id('mobile')}
              type="tel"
              value={get('mobile', mobile?.raw)}
              onChange={set('mobile')}
            />
          </Field>
          <p className="packet__note">The name must match who works the shift.</p>
          <Button
            kind="primary"
            busy={busy}
            onClick={() => {
              void submit({
                givenName: get('givenName', f['givenName']),
                familyName: get('familyName', f['familyName']),
                preferredName: get('preferredName', f['preferredName']),
                email: get('email', email?.raw),
                mobile: get('mobile', mobile?.raw),
              }).then((r) => {
                if (r && r.needsCode.length > 0) setNeedsCode(r.needsCode);
              });
            }}
          >
            Save
          </Button>
        </div>
      );
    }
    case 'reach_you': {
      const m = (f['mailing'] as Record<string, string | null> | null) ?? null;
      return (
        <div className="packet__form">
          <Field id={id('line1')} label="Mailing address (reimbursement checks)">
            <Input
              id={id('line1')}
              value={get('line1', m?.['line1'])}
              onChange={set('line1')}
              placeholder="Street"
            />
          </Field>
          <div className="packet__row">
            <Field id={id('city')} label="City">
              <Input id={id('city')} value={get('city', m?.['city'])} onChange={set('city')} />
            </Field>
            <Field id={id('region')} label="State">
              <Input
                id={id('region')}
                value={get('region', m?.['region'] ?? 'MI')}
                onChange={set('region')}
              />
            </Field>
            <Field id={id('postal')} label="ZIP">
              <Input
                id={id('postal')}
                value={get('postal', m?.['postal'])}
                onChange={set('postal')}
              />
            </Field>
          </div>
          <Field id={id('ecn')} label="Emergency contact — name">
            <Input
              id={id('ecn')}
              value={get('ecn', f['emergencyContactName'])}
              onChange={set('ecn')}
            />
          </Field>
          <Field id={id('ecp')} label="Emergency contact — phone">
            <Input
              id={id('ecp')}
              type="tel"
              value={get('ecp', f['emergencyContactPhone'])}
              onChange={set('ecp')}
            />
          </Field>
          <Field id={id('ecr')} label="Emergency contact — relationship">
            <Input
              id={id('ecr')}
              value={get('ecr', f['emergencyContactRelationship'])}
              onChange={set('ecr')}
              placeholder="spouse, sister…"
            />
          </Field>
          <Button
            kind="primary"
            busy={busy}
            onClick={() => {
              void submit({
                mailing: {
                  line1: get('line1', m?.['line1']),
                  city: get('city', m?.['city']),
                  region: get('region', m?.['region'] ?? 'MI'),
                  postal: get('postal', m?.['postal']),
                },
                emergencyContactName: get('ecn', f['emergencyContactName']),
                emergencyContactPhone: get('ecp', f['emergencyContactPhone']),
                emergencyContactRelationship: get('ecr', f['emergencyContactRelationship']),
              });
            }}
          >
            Save
          </Button>
        </div>
      );
    }
    case 'household': {
      const h = f['household'] as {
        id: string;
        line1: string;
        city: string;
        members: string[];
      } | null;
      return (
        <div className="packet__form">
          {h ? (
            <p>
              On file: <b>{h.members.join(', ')}</b> · {h.line1}, {h.city}
            </p>
          ) : null}
          <Field id={id('search')} label="Find your family by a parent's name">
            <Input
              id={id('search')}
              value={get('search')}
              onChange={(e) => {
                set('search')(e);
                const q = e.target.value.trim();
                if (q.length < 2) {
                  setFamilies([]);
                  return;
                }
                void api<{ householdId: string; names: string[] }[]>(
                  'GET',
                  `/onboarding/packet/households?search=${encodeURIComponent(q)}`,
                ).then(setFamilies);
              }}
            />
          </Field>
          {families.length > 0 ? (
            <ul className="packet__choices" aria-label="Families found">
              {families.map((fam) => (
                <li key={fam.householdId}>
                  <Button
                    busy={busy}
                    onClick={() => {
                      void submit({ householdId: fam.householdId });
                    }}
                  >
                    {fam.names.join(', ')}
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="packet__row">
            {h ? (
              <Button
                kind="primary"
                busy={busy}
                onClick={() => {
                  void submit({ householdId: h.id });
                }}
              >
                Yes, that's my family
              </Button>
            ) : null}
            <Button
              busy={busy}
              onClick={() => {
                void submit({ notASchoolFamily: true });
              }}
            >
              Not a school family
            </Button>
          </div>
        </div>
      );
    }
    case 'role': {
      const roles =
        (f['roles'] as
          { roleName: string; areaName: string | null; title: string | null }[] | undefined) ?? [];
      const co = (f['coChairs'] as { name: string; areaName: string | null }[] | undefined) ?? [];
      const what = roles
        .map((r) =>
          r.areaName ? `${r.title ?? 'chair'} of ${r.areaName}` : r.roleName.toLowerCase(),
        )
        .join(' and ');
      return (
        <div className="packet__form">
          <p>
            This year you are <b>{what || 'a chair'}</b>.{' '}
            {co.length ? `With you: ${co.map((c) => c.name).join(', ')}.` : 'No co-chair yet.'}
          </p>
          <p>Meetings: {(f['cadenceSays'] as string | null) ?? 'the dates are not set yet'}.</p>
          <label className="packet__check">
            <input
              type="checkbox"
              checked={get('willDrive', f['willDrive'] === true ? 'yes' : '') === 'yes'}
              onChange={(e) => {
                setV({ ...v, willDrive: e.target.checked ? 'yes' : '' });
              }}
            />{' '}
            I will drive a festival vehicle or gator
          </label>
          <Button
            kind="primary"
            busy={busy}
            onClick={() => {
              void submit({
                accept: true,
                cadenceAcknowledged: true,
                willDrive: get('willDrive', f['willDrive'] === true ? 'yes' : '') === 'yes',
              });
            }}
          >
            Yes, I'm chairing in {String(packet.festivalYear)} — and I've noted the meetings
          </Button>
        </div>
      );
    }
    case 'waiver': {
      const w = f['waiver'] as {
        name: string;
        version: string;
        text: string | null;
        uploadedAt: string;
      } | null;
      if (!w)
        return (
          <p className="packet__note">
            The volunteer chairs have not posted this year's waiver yet. Come back when it asks you.
          </p>
        );
      return (
        <div className="packet__form">
          <p className="packet__note">
            {w.name} · version {w.version} · {formatDate(w.uploadedAt, { year: true })}
          </p>
          {w.text ? (
            <pre className="packet__waiver">{w.text}</pre>
          ) : (
            <p>
              <a
                href={`/api/onboarding/packet/waiver?year=${String(packet.festivalYear)}`}
                target="_blank"
                rel="noreferrer"
              >
                Open the waiver
              </a>
            </p>
          )}
          <Field id={id('typed')} label="Type your name to sign">
            <Input id={id('typed')} value={get('typedName')} onChange={set('typedName')} />
          </Field>
          <Button
            kind="primary"
            busy={busy}
            onClick={() => {
              void submit({ typedName: get('typedName') });
            }}
          >
            Sign
          </Button>
        </div>
      );
    }
    case 'background_check': {
      const c = f['compliance'] as {
        state: string;
        clearedAt: string | null;
        expiresAt: string | null;
      } | null;
      const needsCheck = f['needsCheck'] !== false;
      /**
       * **Every credential the year asks of them, in one step** (overnight 1c): the background
       * check where it is needed, and each training their role must have — Protecting God's
       * Children — with its state and dates as the volunteer chairs recorded them.
       */
      const trainings =
        (f['trainings'] as
          | {
              name: string;
              state: string | null;
              clearedAt: string | null;
              expiresAt: string | null;
            }[]
          | undefined) ?? [];
      return (
        <div className="packet__form">
          {needsCheck && c ? (
            <p>
              Background check:{' '}
              <Chip kind={c.state === 'cleared' ? 'ok' : 'warn'}>{c.state.replace('_', ' ')}</Chip>
              {c.expiresAt ? ` · good until ${formatDate(c.expiresAt, { year: true })}` : ''}
            </p>
          ) : null}
          {trainings.map((t) => (
            <p key={t.name}>
              {t.name}:{' '}
              {t.state ? (
                <Chip kind={t.state === 'cleared' ? 'ok' : 'warn'}>
                  {t.state.replace('_', ' ')}
                </Chip>
              ) : (
                <Chip kind="warn">not on file</Chip>
              )}
              {t.clearedAt ? ` · ${formatDate(t.clearedAt, { year: true })}` : ''}
              {t.expiresAt ? ` · good until ${formatDate(t.expiresAt, { year: true })}` : ''}
            </p>
          ))}
          <p>
            CIDER never runs a check or a class; the school does, and the volunteer chairs record
            its answer with the dates.
          </p>
          {needsCheck ? (
            <Button
              kind="primary"
              busy={busy}
              onClick={() => {
                void submit({ consent: true });
              }}
            >
              I consent to the school's background check
            </Button>
          ) : null}
        </div>
      );
    }
    case 'driving': {
      const lic = f['license'] as {
        stateCode: string;
        expiresOn: string;
        numberMasked: string;
      } | null;
      return (
        <div className="packet__form">
          {lic ? (
            <p>
              On file: {lic.numberMasked} · {lic.stateCode} · expires{' '}
              {formatCalendarDate(lic.expiresOn)}
            </p>
          ) : null}
          <Field id={id('number')} label="Driver's license number" hint="kept sealed; shown masked">
            <Input id={id('number')} value={get('number')} onChange={set('number')} />
          </Field>
          <div className="packet__row">
            <Field id={id('state')} label="State">
              <Input
                id={id('state')}
                value={get('stateCode', lic?.stateCode ?? 'MI')}
                onChange={set('stateCode')}
              />
            </Field>
            <Field id={id('exp')} label="Expires">
              <Input
                id={id('exp')}
                type="date"
                value={get('expiresOn', lic?.expiresOn)}
                onChange={set('expiresOn')}
              />
            </Field>
          </div>
          <Button
            kind="primary"
            busy={busy}
            onClick={() => {
              void submit({
                number: get('number'),
                stateCode: get('stateCode', lic?.stateCode ?? 'MI'),
                expiresOn: get('expiresOn', lic?.expiresOn),
                mvrConsent: true,
              });
            }}
          >
            Save and consent to the record check
          </Button>
        </div>
      );
    }
    case 'over_21':
      return (
        <div className="packet__form">
          <p>No date of birth is stored. The volunteer chairs tick “ID seen” when it sees yours.</p>
          <Button
            kind="primary"
            busy={busy}
            onClick={() => {
              void submit({ attest: true });
            }}
          >
            I am 21 or over
          </Button>
        </div>
      );
    case 'keys_access': {
      const keys =
        (f['keys'] as
          | {
              id: string;
              itemName: string;
              number: string;
              issuedByName: string;
              issuedAt: string;
            }[]
          | undefined) ?? [];
      const strike = f['strikeDate'] as string | null;
      return (
        <div className="packet__form">
          {keys.length === 0 ? (
            <p>Nothing has been issued to you yet. Whatever is, shows here from the key log.</p>
          ) : (
            <ul className="packet__keys">
              {keys.map((k) => (
                <li key={k.id}>
                  <b>{k.itemName}</b> {k.number} · from {k.issuedByName},{' '}
                  {formatDateTime(k.issuedAt)}
                </li>
              ))}
            </ul>
          )}
          <Button
            kind="primary"
            busy={busy}
            onClick={() => {
              void submit({ received: true });
            }}
          >
            I received these and will return them by{' '}
            {strike ? formatCalendarDate(strike) : 'the strike'}
          </Button>
        </div>
      );
    }
    case 'four_things': {
      const four = (f['fourThings'] as { what: string; where: string }[] | undefined) ?? [];
      return (
        <div className="packet__form">
          <ol>
            {four.map((t) => (
              <li key={t.what}>
                {t.what} <span className="packet__note">— {t.where}</span>
              </li>
            ))}
          </ol>
          <Button
            kind="primary"
            busy={busy}
            onClick={() => {
              void submit({ acknowledged: true });
            }}
          >
            Got it — four taps, nothing off-season
          </Button>
        </div>
      );
    }
    case 'done': {
      const open = packet.items.filter(
        (i) =>
          i.required &&
          i.key !== 'done' &&
          i.state !== 'done' &&
          i.state !== 'not applicable' &&
          i.state !== 'waiting',
      );
      return (
        <div className="packet__form">
          {open.length ? (
            <p>Still open: {open.map((i) => i.name).join(', ')}.</p>
          ) : (
            <p>
              Everything is in. One tap and the packet is done for {String(packet.festivalYear)}.
            </p>
          )}
          <Button
            kind="primary"
            busy={busy}
            onClick={() => {
              void submit({});
            }}
          >
            Done
          </Button>
        </div>
      );
    }
    default:
      return null;
  }
}

/** A finished item's one line. */
function Summary({ item }: { item: PacketItem }): ReactNode {
  const f = item.facts;
  switch (item.key) {
    case 'who_you_are':
      return `${text(f['givenName'])} ${text(f['familyName'])}${text(f['preferredName']) ? ` (${text(f['preferredName'])})` : ''}`;
    case 'reach_you': {
      const m = f['mailing'] as Record<string, string | null> | null;
      return m
        ? `${m['line1'] ?? ''}, ${m['city'] ?? ''} · ${text(f['emergencyContactName'])}`
        : '';
    }
    case 'household': {
      const h = f['household'] as { members: string[] } | null;
      return h ? h.members.join(', ') : 'not a school family';
    }
    case 'waiver':
      return item.signature
        ? `signed ${item.signature.typedName}, ${formatDateTime(item.signature.at)}`
        : '';
    case 'driving': {
      const lic = f['license'] as { numberMasked: string; stateCode: string } | null;
      return lic ? `${lic.numberMasked} · ${lic.stateCode}` : '';
    }
    default:
      return item.doneAt ? formatDateTime(item.doneAt) : '';
  }
}

export function PacketScreen({
  onDone,
  readOnly = false,
}: {
  onDone?: () => void;
  /** A director viewing as the chair: the items and what they say, and no form to fill. */
  readOnly?: boolean;
}): ReactNode {
  const load = useLoad<Packet>('/onboarding/packet');
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const p = load.data;
  // The first open item opens itself; after a save the next one does.
  useEffect(() => {
    if (!p || open !== null || readOnly) return;
    const next = p.items.find(
      (i) => i.required && i.state !== 'done' && i.state !== 'not applicable',
    );
    setOpen(next?.key ?? null);
  }, [p, open]);
  if (load.error)
    return (
      <p className="ui-form__refusal" role="alert">
        {load.error}
      </p>
    );
  if (!p) return <p>Reading your packet…</p>;
  const submit =
    (key: string) =>
    async (payload: Record<string, unknown>): Promise<Completion | null> => {
      setBusy(true);
      setRefusal(null);
      try {
        if (payload['__reload']) {
          load.reload();
          const fresh = await api<Packet>('GET', '/onboarding/packet');
          const next = fresh.items.find(
            (i) => i.required && i.state !== 'done' && i.state !== 'not applicable',
          );
          setOpen(next?.key ?? null);
          return null;
        }
        const r = await api<Completion>('POST', `/onboarding/packet/${key}`, {
          ...payload,
          year: p.festivalYear,
        });
        load.reload();
        if (r.needsCode.length > 0) return r;
        const next = r.packet.items.find(
          (i) =>
            i.required &&
            i.state !== 'done' &&
            i.state !== 'not applicable' &&
            i.state !== 'waiting',
        );
        setOpen(next?.key ?? null);
        if (key === 'done' && r.item.state === 'done' && onDone) onDone();
        return r;
      } catch (e: unknown) {
        setRefusal(sentence(e));
        return null;
      } finally {
        setBusy(false);
      }
    };
  const required = p.items.filter((i) => i.required);
  return (
    <div className="packet" data-screen="packet">
      <p className="packet__lede" role="status">
        <b>{p.says}</b>
        {p.required > 0
          ? ` · ${String(p.percent)}% · ${String(p.done)} of ${String(p.required)}`
          : ''}
      </p>
      {p.left === 0 && p.required > 0 ? (
        <p>
          <a href={`/print/packet/${String(p.festivalYear)}`} target="_blank" rel="noreferrer">
            Print the summary
          </a>
        </p>
      ) : null}
      {refusal ? (
        <p className="ui-form__refusal" role="alert">
          {refusal}
        </p>
      ) : null}
      <ol className="packet__items">
        {/*
          **Item 1, "Your account"** (James, Oct 2): a person with no account when they said yes
          set one up from the packet mail's link — that page was this item. Everybody else's
          packet is the eleven as built.
        */}
        {p.account ? (
          <li className="packet__item" data-item="account">
            <span className="packet__head">
              <span className="packet__n">1</span>
              <span className="packet__name">Your account</span>
              <Chip kind="ok">done</Chip>
            </span>
            <p className="packet__summary">{`Set up ${formatDateTime(p.account.setUpAt)}.`}</p>
          </li>
        ) : null}
        {required.map((item, i) => {
          const isOpen = open === item.key;
          return (
            <li
              key={item.key}
              className={`packet__item${isOpen ? ' packet__item--open' : ''}`}
              data-item={item.key}
            >
              <button
                type="button"
                className="packet__head"
                aria-expanded={isOpen}
                onClick={() => {
                  setOpen(isOpen ? null : item.key);
                }}
              >
                <span className="packet__n">{String(i + (p.account ? 2 : 1))}</span>
                <span className="packet__name">{item.name}</span>
                <Chip kind={STATE_CHIP[item.state]}>{STATE_WORDS[item.state]}</Chip>
              </button>
              {isOpen ? (
                <div className="packet__body">
                  <p className="packet__asks">{item.asks}</p>
                  {item.state === 'done' || item.state === 'not applicable' ? (
                    <p className="packet__summary">
                      <Summary item={item} />
                      {item.carriedFrom ? ` · carried from ${String(item.carriedFrom)}` : ''}
                      {item.recordedByName ? ` · recorded by ${item.recordedByName}` : ''}
                      {item.naReason ? ` · ${item.naReason}` : ''}
                    </p>
                  ) : null}
                  {item.state === 'waiting' ? (
                    <p className="packet__summary">Your part is in; the volunteer chairs answer.</p>
                  ) : null}
                  {/* Viewing as the chair, nothing is pressed: no form, no Save (James, Oct 4). */}
                  {!readOnly && item.state !== 'not applicable' && item.state !== 'waiting' ? (
                    <ItemForm item={item} packet={p} submit={submit(item.key)} busy={busy} />
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * The chair app's packet line (`Chairs.dc.html`, under the bar on every screen): "Finish your 2027
 * packet — 3 items left", with Open — a white band across the screen, as drawn.
 */
export function PacketBanner({ onGo }: { onGo: (href: string) => void }): ReactNode {
  const load = useLoad<Packet>('/onboarding/packet');
  const p = load.data;
  if (!p || p.required === 0) return null;
  return (
    <div
      className={`dr__packet${p.left === 0 ? ' dr__packet--done' : ''}`}
      data-testid="packet-banner"
    >
      <div className="dr__packetIn">
        <TabGlyph name="clipboard-check" className="dr__packetIcon" />
        <span className="dr__packetText">{p.says}</span>
        <button
          type="button"
          className="dr__act"
          onClick={() => {
            onGo('/app/more/your-packet');
          }}
        >
          Open
        </button>
      </div>
    </div>
  );
}
